/**
 * Placement d'un popover autour de son bouton — la seule vraie décision de
 * géométrie de GooeyPopover, sortie du composant pour être vérifiable.
 *
 * Deux règles, et elles comptent :
 *   — le côté demandé n'est qu'une préférence. Il cède quand la place manque,
 *     sinon un bouton posé en haut de l'écran ouvre son panneau hors champ ;
 *   — la hauteur se recale sur la place réellement disponible, sinon le panneau
 *     recouvre le bouton dont il est censé sortir.
 */

export type Anchor = { top: number; bottom: number; left: number; right: number };
export type Placed = { dir: 'above' | 'below' | 'side'; x: number; y: number; h: number };

/** En deçà, le côté préféré est jugé trop à l'étroit et on regarde l'autre. */
const COMFORT = 240;
/** Le panneau ne se réduit jamais sous cette hauteur, même sans place. */
const FLOOR = 180;
/** Marge minimale au bord de l'écran. */
const EDGE = 8;
const SIDE_EDGE = 10;

export function placePanel({ anchor, W, H, width, maxHeight, vhCap, placement, align, gap, topInset }: {
  anchor: Anchor;
  W: number; H: number;
  width: number; maxHeight: number; vhCap: number;
  /** `side` : le panneau se pose À CÔTÉ de l'ancre et prend toute la hauteur
   *  disponible. C'est le seul placement tenable en paysage, où l'écran est bas :
   *  au-dessus ou au-dessous, il ne resterait qu'une bande. */
  placement: 'above' | 'below' | 'side';
  align: 'center' | 'start' | 'end';
  /** Écart de matière entre la goutte du bouton et celle du panneau. Vient de
   *  l'appelant : c'est lui qui règle l'épaisseur du filet de fusion. */
  gap: number;
  /** Marge réservée EN HAUT de l'écran. `EDGE` (8 px) suffit à un petit menu,
   *  pas à un panneau plein écran : il passerait sous les boutons flottants du
   *  lab (jour/nuit). L'appelant, seul à savoir ce qui flotte au-dessus, la
   *  donne. */
  topInset?: number;
}): Placed {
  const TOP = topInset ?? EDGE;
  if (placement === 'side') {
    // Toute la hauteur, moins la marge de bord : c'est justement la dimension
    // qui manque en paysage, il n'y a rien à en laisser.
    const h = Math.min(maxHeight, H - 2 * EDGE);
    // Strictement À CÔTÉ du bouton, séparé du même écart de matière que dans
    // les autres placements : le panneau prend toute la hauteur, il croiserait
    // donc le bouton s'il s'alignait sur le bord de l'encart.
    //
    // Le CÔTÉ est déduit de la place, pas imposé : en paysage le bouton passe à
    // gauche ou à droite selon le sens dans lequel l'appareil a été tourné.
    // Forcer un côté revenait à écraser le bouton une fois sur deux.
    const roomLeft = anchor.left - gap - SIDE_EDGE;
    const roomRight = W - anchor.right - gap - SIDE_EDGE;
    const toRight = align === 'start' || (align !== 'end' && roomRight > roomLeft);
    const raw = toRight ? anchor.right + gap : anchor.left - gap - width;
    return {
      dir: 'side',
      x: Math.min(Math.max(raw, SIDE_EDGE), W - width - SIDE_EDGE),
      y: Math.max((H - h) / 2, EDGE),
      h,
    };
  }

  const roomAbove = anchor.top - gap - TOP;
  const roomBelow = H - anchor.bottom - gap - EDGE;
  const [want, other] = placement === 'below' ? [roomBelow, roomAbove] : [roomAbove, roomBelow];
  const keep = want >= COMFORT || want >= other;
  const dir = keep ? placement : placement === 'below' ? 'above' : 'below';

  const h = Math.min(H * vhCap, maxHeight, Math.max(keep ? want : other, FLOOR));

  const raw = align === 'end' ? anchor.right - width
    : align === 'start' ? anchor.left
    : (anchor.left + anchor.right) / 2 - width / 2;
  const x = Math.min(Math.max(raw, SIDE_EDGE), W - width - SIDE_EDGE);

  const y = dir === 'below'
    ? Math.min(anchor.bottom + gap, H - h - EDGE)
    : Math.max(anchor.top - gap - h, EDGE);

  return { dir, x, y, h };
}
